import React, { useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'grey.900',
        backgroundImage: `
          radial-gradient(circle at 50% 115%, ${alpha(theme.palette.warning.light, 0.24)} 0%, transparent 40%),
          radial-gradient(circle at 8% 8%, ${alpha(theme.palette.error.main, 0.28)} 0%, transparent 32%),
          repeating-conic-gradient(from -90deg at 50% 120%, ${alpha(theme.palette.warning.main, 0.24)} 0deg 2deg, transparent 2deg 11deg),
          repeating-linear-gradient(90deg, transparent 0 22px, ${alpha(theme.palette.warning.light, 0.08)} 22px 24px),
          linear-gradient(155deg, ${alpha(theme.palette.error.dark, 0.98)} 0%, ${alpha(theme.palette.error.dark, 0.9)} 58%, ${alpha(theme.palette.error.main, 0.82)} 100%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 18,
          border: `1px solid ${alpha(theme.palette.warning.light, 0.52)}`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 30,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.error.light, 0.2)}`,
          pointerEvents: 'none',
        },
      })}
    >
      <Paper elevation={8} sx={{ p: 6, maxWidth: 400, width: '100%', textAlign: 'center', borderRadius: 3, bgcolor: 'black' }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Decade On Demand"
          sx={{ width: 300, maxWidth: '100%', mb: 1, mx: 'auto', display: 'block' }}
        />

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={(theme) => ({
            color: theme.palette.common.white,
            background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 55%, ${theme.palette.warning.main} 100%)`,
            border: `1px solid ${alpha(theme.palette.warning.light, 0.5)}`,
            boxShadow: `0 8px 20px ${alpha(theme.palette.error.dark, 0.45)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 50%, ${theme.palette.warning.dark} 100%)`,
              boxShadow: `0 10px 24px ${alpha(theme.palette.error.dark, 0.55)}`,
            },
            '&:disabled': {
              background: alpha(theme.palette.error.dark, 0.5),
              color: alpha(theme.palette.common.white, 0.6),
              borderColor: alpha(theme.palette.warning.light, 0.25),
            },
            py: 1.5,
            gap: 1.5,
          })}
          startIcon={
            loading ? (
              <CircularProgress size={18} sx={(theme) => ({ color: alpha(theme.palette.common.white, 0.8) })} />
            ) : (
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#ffffff" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#ffffff" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#ffffff" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#ffffff" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
            )
          }
        >
          {loading ? 'Signing in…' : 'Sign in with Google'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}

export default Login;
